import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { CheckBox, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { isMobile } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';
import { CardCompareCheckboxProps } from './CardCompareCheckbox.types';
import { StyledCardCompareCheckbox } from './CardCompareCheckbox.styles';

/**
 * CardCompareCheckbox - Handles card comparison selection
 * Maximum 3 cards on desktop, 2 on mobile
 */
export const CardCompareCheckbox = memo<CardCompareCheckboxProps>(
  ({ cardCode, cardType, imagePath, imagePathWithoutBadge, cardHeader, componentId, customClass }) => {
    const { cobrandData, setCobrandData } = useStore(store => store);
    const [isChecked, setIsChecked] = useState(false);
    const isViewportL = useCheckBreakpoint('viewportL');
    const isMobileDevice = isMobile();

    // Sync checkbox state with store
    useEffect(() => {
      const checked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
      setIsChecked(checked);
    }, [cobrandData.selectedCards, cardCode]);

    // Calculate if checkbox should be disabled (max limit reached)
    const isDisabled = useMemo(() => {
      const isAlreadySelected = cobrandData.selectedCards.some(item => item.cardname === cardCode);
      const maxCards = isViewportL && !isMobileDevice ? 3 : 2;
      return cobrandData.selectedCards.length >= maxCards && !isAlreadySelected;
    }, [cobrandData.selectedCards, cardCode, isViewportL, isMobileDevice]);

    // Handle checkbox toggle
    const handleCheckboxChange = useCallback(() => {
      setIsChecked(prev => {
        const newState = !prev;

        // Update selected cards array
        const updatedCards = newState
          ? [
              ...cobrandData.selectedCards,
              {
                cardname: cardCode,
                checked: newState,
                cardType,
                imagePath: imagePathWithoutBadge ? imagePathWithoutBadge : imagePath,
                header: cardHeader,
              },
            ]
          : cobrandData.selectedCards.filter(card => card.cardname !== cardCode);

        setCobrandData({
          ...cobrandData,
          selectedCards: updatedCards,
        });

        return newState;
      });
    }, [cobrandData, setCobrandData, cardCode, cardType, imagePath, imagePathWithoutBadge, cardHeader]);

    return (
      <StyledCardCompareCheckbox
        className={clsx(customClass)}
        data-component-name="m-cobrand-CardCompareCheckbox"
        data-testid="cobrand-CardCompareCheckbox"
      >
        <CheckBox
          checkboxLabel="Compare"
          aria-label={`compare-${componentId}-checkbox`}
          onChange={handleCheckboxChange}
          checked={isChecked}
          checkboxName={`${componentId}-checkbox`}
          checkboxId={`${componentId}-checkbox`}
          disabled={isDisabled}
        />
      </StyledCardCompareCheckbox>
    );
  }
);

CardCompareCheckbox.displayName = 'CardCompareCheckbox';
